import {
  Component,
  inject,
  signal,
  OnInit,
  ChangeDetectionStrategy,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  DialogRef,
  InputComponent,
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
} from 'nebula-ui-kit';
import { PerfilService, PerfilData } from '@nebula-shared/data-access';

/** Literales (i18n) de los tipos de documento, para mostrar el label y no el código crudo. */
const TIPO_DOC_LABELS: Record<string, string> = {
  CC: 'Cédula de Ciudadanía (CC)',
  RC: 'Registro Civil (RC)',
  TI: 'Tarjeta de Identidad (TI)',
  PP: 'Pasaporte (PP)',
  NIT: 'NIT',
  CE: 'Cédula de Extranjería (CE)',
  PEP: 'PEP',
  IE: 'Identificador Extranjero (IE)',
};

/**
 * Modal de PERFIL propio (autoservicio, patrón estándar de la app: `lib-dialog` + cabecera). Se abre
 * desde la opción "Perfil" del menú del header. Solo permite editar datos personales: nombres,
 * apellidos y correo. Usuario, tipo y número de documento se muestran de solo lectura.
 */
@Component({
  selector: 'app-perfil-modal',
  standalone: true,
  templateUrl: './perfil-modal.component.html',
  styleUrls: ['./perfil-modal.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerfilModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly perfilService = inject(PerfilService);
  private readonly dialogRef = inject<DialogRef<PerfilModalComponent, PerfilData | null>>(
    DialogRef,
    {
      optional: true,
    },
  );

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMsg = signal('');

  /** Perfil cargado; se reenvía completo al guardar (el backend solo aplica los 3 campos editables). */
  private loaded: PerfilData | null = null;

  readonly form = this.fb.group({
    username: [{ value: '', disabled: true }],
    tipoDocumento: [{ value: '', disabled: true }],
    numeroDocumento: [{ value: '', disabled: true }],
    nombres: ['', [Validators.required, Validators.maxLength(100)]],
    apellidos: ['', [Validators.required, Validators.maxLength(100)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(120)]],
  });

  ngOnInit(): void {
    this.perfilService.getMiPerfil().subscribe({
      next: (perfil) => {
        this.patch(perfil);
        this.loading.set(false);
      },
      error: () => {
        this.errorMsg.set('No se pudo cargar tu perfil.');
        this.loading.set(false);
      },
    });
  }

  private patch(perfil: PerfilData): void {
    this.loaded = perfil;
    const codigo = perfil.tipoDocumentoCodigo ?? '';
    this.form.patchValue({
      username: perfil.username ?? '',
      tipoDocumento: TIPO_DOC_LABELS[codigo] ?? codigo,
      numeroDocumento: perfil.numeroDocumento ?? '',
      nombres: perfil.nombres ?? '',
      apellidos: perfil.apellidos ?? '',
      email: perfil.email ?? '',
    });
  }

  getError(key: string): string {
    const control = this.form.get(key);
    if (!control || !control.touched) return '';
    if (control.hasError('required')) return 'Campo requerido';
    if (control.hasError('email')) return 'Correo inválido';
    if (control.hasError('maxlength')) {
      return `Máximo ${control.errors?.['maxlength']?.requiredLength} caracteres`;
    }
    return '';
  }

  guardar(): void {
    if (this.form.invalid || !this.loaded) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.errorMsg.set('');
    const dto: PerfilData = {
      ...this.loaded,
      nombres: this.form.value.nombres ?? '',
      apellidos: this.form.value.apellidos ?? '',
      email: this.form.value.email ?? '',
    };
    this.perfilService.actualizarMiPerfil(dto).subscribe({
      next: (perfil) => {
        this.saving.set(false);
        this.dialogRef?.close(perfil ?? dto);
      },
      error: () => {
        this.saving.set(false);
        this.errorMsg.set('No se pudieron guardar los cambios. Intenta nuevamente.');
      },
    });
  }

  cerrar(): void {
    this.dialogRef?.close(null);
  }
}
